import { FeatureFlagIds } from "../../../utils/featureFlagIds";
import {
  ApplicationFormError,
  DealerApplication,
  DealerApplicationCustomerEmployment,
  DealerApplicationForm,
  OtherIncome,
  getNumberValue,
  hasValue,
  LookupItem
} from '@amsanalytics/react-shared-library';
import { Alert, Button, Card, Snackbar, Tooltip } from '@mui/material';
import React, {JSX, useState} from 'react';
import { useSelector } from 'react-redux';
import styles from './_dealer-application-form-dialog.module.scss';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { isNil, reject } from 'ramda';
import { NamedId } from '../../../Interfaces/NamedId';
import { capitalizeFirstLetter, removeHyphensFromString, splitCamelCaseWord } from '../../../utils/utils';
import { AppState } from '../../../store/rootReducer';
import { DealerApplicationCustomerWithPrimaryFlag, DealerApplicationSubmission } from '../../../Interfaces/DealerApplication/DealerApplicationSubmission';
import { defaultDealerApplication } from '../../../Interfaces/DealerApplication/DealerApplicationForm';
import { Constants } from '../../../utils/constants';
import { DialogProps } from '../DialogProps';
import { DecodedVehicleInformation } from '../../../Data/DecodedVehicleInformation';
import { useConfiguration } from '../../../Data/ConfigurationData';

const mockCustomerTitles: NamedId[] = [
  {
    id: '1',
    name: 'Mr.',
  },
  {
    id: '2',
    name: 'Ms.',
  },
  {
    id: '3',
    name: 'Mrs.',
  },
];

const suffixes: NamedId[] = [
  { id: '1', name: 'Jr.' },
  { id: '2', name: 'Sr.' },
  { id: '3', name: 'I' },
  { id: '4', name: 'II' },
  { id: '5', name: 'III' },
];

const mockVehicleIntendedUses: NamedId[] = [
  { id: '1', name: 'Personal' },
  { id: '2', name: 'Business' },
  { id: '3', name: 'Agricultural' },
  { id: '3', name: 'Hazardous' },
  { id: '3', name: 'Local' },
  { id: '3', name: 'Interstate' },
  { id: '3', name: 'Intermediate' },
];

// Displays list of errors in the form
const DealerFormErrorList = (props: { errors: ApplicationFormError[] }): JSX.Element => {
  return <Card style={{ background: 'white', margin: '-4px', marginLeft: '-8px', marginRight: '-8px', padding: '10px', fontSize: '14px', maxHeight: '300px', width: '200px', overflowY: 'auto' }}>
    <div style={{ marginBottom: '3px' }}>{props.errors.length} required fields:</div>
    {props.errors.map((error: ApplicationFormError, idx: number) => {
      return <div key={idx} style={{ display: 'flex', flexDirection: 'column', marginTop: 0 }}>
        <div style={{ color: '#a02a2e', marginTop: 2, marginBottom: 2, fontWeight: 500, width: 'calc(100% - 10px)', padding: 5, border: 'solid 1px red', background: '#fef3f3' }}>
          {splitCamelCaseWord(capitalizeFirstLetter(error.error))}
        </div>
      </div>
    })}
  </Card>;
};

export interface DealerApplicationFormDialogProps extends DialogProps<DealerApplicationSubmission> {
  clientId: string;
  defaultLocationId?: string;
}

export default function DealerApplicationFormDialog(props: DealerApplicationFormDialogProps) {
  // List of states to populate dropdowns
  const states = useSelector((appState: AppState) => appState.applications.states);
  // list of IDs to filter the list of applicant addresses to show in the dropdown
  const applicantAddressStateIdsToFilterBy = useSelector((appState: AppState) => appState.applications.lookups.applicantAddressStateIdsToFilterBy);
  // list of IDs to filter the list of employment addresses to show in the dropdown
  const employerAddressStateIdsToFilterBy = useSelector((appState: AppState) => appState.applications.lookups.employerAddressStateIdsToFilterBy);
  // List of locations to populate dropdowns
  const housingStatuses = useSelector((appState: AppState) => appState.applications.housingStatuses);
  // List of income periods to populate dropdowns
  const incomePeriods = useSelector((appState: AppState) => appState.applications.incomePeriods);
  // List of bookout sources to populate dropdowns
  const bookoutSources = useSelector((appState: AppState) => appState.applications.lookups.bookoutSources);
  // List of income types to populate dropdowns
  const incomeTypes = useSelector((appState: AppState) => appState.applications.incomeTypes);
  // All Organizations
  const allOrganizations = useSelector((appState: AppState) => appState.applications.lookups.organizations);
  // All job codes
  const jobCodes = useSelector((appState: AppState) => appState.applications.jobCodes);
  // Minimum months of address
  const minMonthsAddress = useSelector((appState: AppState) => appState.applications.lookups.minimumMonthsOfAddressInformation);
  // Minimum months of employment
  const minMonthsEmployment = useSelector((appState: AppState) => appState.applications.lookups.minimumMonthsOfEmploymentInformation);
  // use payment call as default dealer application
  const usePaymentCallTransactionType = useSelector((appState: AppState) => appState.applications.lookups.usePaymentCallTransactionType);
  // Get disclosure text from lookup items
  const disclosureText = useSelector((state: AppState) => state.applications.lookups.disclosure);
  // Dealer application form
  const [dealerApp, setDealerApp] = useState<DealerApplication>({ ...defaultDealerApplication, transactionTypeId: '1', locationId: props.defaultLocationId || '' });
  // Form errors
  const [formErrors, setFormErrors] = useState<ApplicationFormError[]>([]);
  // Whether or not terms and conditions have been accepted
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  // Whether or not we are decoding the VIN
  const [isDecodingVin, setIsDecodingVin] = useState<boolean>(false);
  // Whether or not the decode Vin snack bar is open
  const [isDecodeSnackbarOpen, setIsDecodeSnackbarOpen] = useState<boolean>(false);
  // VIN decode snackbar message
  const [decodeSnackbarMessage, setDecodeSnackbarMessage] = useState<string>('');
  const { data: configuration } = useConfiguration();
  const [disableSave, setdisabledSave] = useState<boolean>(false);

  const relationshipToApplicantTypes = useSelector((appState: AppState) => appState.applications.lookups.relationshipToApplicants);

  // Try to get vehicle information from VIN entered by user. This occurs when user clicks the 'Decode VIN' button in the app form.
  const getVehicleInformationByVin = async () => {
    try {
      setIsDecodingVin(true);
      const response = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin/${dealerApp.saleVehicle.vin}?format=json`);
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      // Pluck out the year, make, and model from the response
      const vinInfo: { Results: { Value: string, ValueId: string, Variable: string, VariableId: number }[] } = await response.json();
      // Determine if there was an error decoding the VIN
      const hasError = vinInfo?.Results.find(r => r.VariableId === 143)?.Value !== "0";

      // If there was no error, update the year, make, and model
      if (!hasError) {
        const yearResult = vinInfo.Results.find(r => r.VariableId === 29)?.Value;
        const make: string = vinInfo.Results.find(r => r.VariableId === 26)?.Value as string;
        const model: string = vinInfo.Results.find(r => r.VariableId === 28)?.Value as string;
        const year: number = yearResult ? parseInt(yearResult) : 0;
        return { year, make, model };
      } else {
        // If there was an error, show a snackbar with the error message
        return undefined;
      }
    } catch (error: any) {
      // catch any errors and show a snackbar with the error message
      return undefined;
    }
  }

  // User clicks 'Decode VIN' button
  const handleDecodeVinClick = async () => {
    await getVehicleInformationByVin().then((decodedInfo: DecodedVehicleInformation | undefined) => {
      if (!isNil(decodedInfo)) {
        // Update Sale Vehicle in the Dealer Application
        setDealerApp({
          ...dealerApp,
          saleVehicle: {
            ...dealerApp.saleVehicle,
            year: decodedInfo.year,
            make: decodedInfo.make,
            model: decodedInfo.model,
          }
        });
        let newErrors = [...formErrors];
        // Remove year, make, and model from the form errors if a value was successfully decoded
        if (hasValue(decodedInfo.year)) {
          newErrors = reject((fe: ApplicationFormError) => fe.fieldName.toUpperCase() === 'YEAR', newErrors);
        }
        if (hasValue(decodedInfo.make)) {
          newErrors = reject((fe: ApplicationFormError) => fe.fieldName.toUpperCase() === 'MAKE', newErrors);
        }
        if (hasValue(decodedInfo.model)) {
          newErrors = reject((fe: ApplicationFormError) => fe.fieldName.toUpperCase() === 'MODEL', newErrors);
        }
        setFormErrors(newErrors);
        // Show snackbar with success message
        setDecodeSnackbarMessage('VIN decoded successfully.');
      } else {
        // Show snackbar with error message
        setDecodeSnackbarMessage('VIN could not be decoded. Please double-check the VIN and try again.');
      }
    }).finally(() => {
      setIsDecodeSnackbarOpen(true);
      setIsDecodingVin(false);
    });
  };

  // Returns true if the user can submit the form, false otherwise. User can submit if:
  // 1. There are no form errors
  // 2. Terms and conditions have been accepted
  // 3. Total months of current address are greater than or equal to the minimum months of address information
  // 4. Total months of current employment are greater than or equal to the minimum months of employment information
  const canSubmitForm = () => {
    return formErrors.length === 0 && termsAccepted
  };

  const calculateAmountToFinance = (): number => {
    return getNumberValue(dealerApp.contractInformation.cashPrice) +
      getNumberValue(dealerApp.contractInformation.taxes) +
      getNumberValue(dealerApp.contractInformation.docFees) +
      getNumberValue(dealerApp.contractInformation.titleRegistrationFees) -
      getNumberValue(dealerApp.contractInformation.cashDown) -
      getNumberValue(dealerApp.contractInformation.rebate) -
      getNumberValue(dealerApp.contractInformation.tradeAllowance) +
      getNumberValue(dealerApp.contractInformation.tradeOwed) +
      getNumberValue(dealerApp.contractInformation.gap) +
      getNumberValue(dealerApp.contractInformation.serviceContract) +
      getNumberValue(dealerApp.contractInformation.creditLife) +
      getNumberValue(dealerApp.contractInformation.disability) +
      getNumberValue(dealerApp.contractInformation.otherInsuranceOrService);
  };

  // User has clicked the 'Submit' button
  const handleSubmitClick = () => {
    // Massage dealer app object before submitting. A few things to do:
    // Map IDs to their corresponding vales for necessary properties.
    // If no co-applicant, set the co-applicant object to undefined
    // If 'Payment Call' transaction type, set Sale Vehicle, Trade Vehicle, and Contract Info to undefined
    const appCustomers: DealerApplicationCustomerWithPrimaryFlag[] = [{
      ...dealerApp.applicant,
      isPrimary: true,
      title: mockCustomerTitles.find((title: NamedId) => title.id === dealerApp.applicant.title)?.name,
      suffix: suffixes.find((suffix: NamedId) => suffix.id === dealerApp.applicant.suffix)?.name,
      ssn: removeHyphensFromString(dealerApp.applicant.ssn),
      housingStatusId: dealerApp.applicant.housingStatusId,
      addresses: [
        ...dealerApp.applicant.addresses.map(a => ({ ...a, state: hasValue(a.state) ? a.state : undefined }))
      ],
      employments: dealerApp.applicant.employments.map((e: DealerApplicationCustomerEmployment) => {
        return {
          ...e,
          state: e.state || undefined
        }
      }),
      otherIncomes: dealerApp.applicant.otherIncomes.map((io: OtherIncome) => {
        return {
          amount: io.amount,
          intervalId: io.intervalId,
          sourceId: io.sourceId,
          startDate: io.startDate,
          endDate: io.endDate,
          comments: io.comments,
        };
      }),
    }];
    if (!isNil(dealerApp.coApplicant)) {
      appCustomers.push({
        ...dealerApp.coApplicant,
        isPrimary: false,
        relationshipCode: relationshipToApplicantTypes.find((relationship: LookupItem) => relationship.id === dealerApp.coApplicant?.relationshipCode)?.id,
        title: mockCustomerTitles.find((title: NamedId) => title.id === dealerApp.coApplicant?.title)?.name,
        suffix: suffixes.find((suffix: NamedId) => suffix.id === dealerApp.coApplicant?.suffix)?.name,
        ssn: removeHyphensFromString(dealerApp.coApplicant.ssn),
        housingStatusId: dealerApp.coApplicant!.housingStatusId,
        addresses: [
          ...dealerApp.coApplicant.addresses.map(a => ({ ...a, state: hasValue(a.state) ? a.state : undefined, }))
        ],
        employments: dealerApp.coApplicant.employments.map((e: DealerApplicationCustomerEmployment) => {
          return {
            ...e,
            state: e.state || undefined,
          }
        }),
        otherIncomes: dealerApp.coApplicant.otherIncomes.map((io: OtherIncome) => {
          return {
            amount: io.amount,
            intervalId: io.intervalId,
            sourceId: io.sourceId,
            startDate: io.startDate,
            endDate: io.endDate,
            comments: io.comments,
          };
        }),
      });
    }
    const massagedDealerApp: DealerApplicationSubmission = {
      locationId: dealerApp.locationId,
      customers: appCustomers,
      saleVehicle: dealerApp.transactionTypeId === '2' ? undefined : { ...dealerApp.saleVehicle },
      tradeVehicle: dealerApp.transactionTypeId === '2' ? undefined : isNil(dealerApp.tradeVehicle) ? undefined : { ...dealerApp.tradeVehicle },
      contractInformation: dealerApp.transactionTypeId === '2' ? undefined : isNil(dealerApp.contractInformation) ? undefined : { ...dealerApp.contractInformation, amountToFinance: calculateAmountToFinance() },
      comments: dealerApp.comments ? dealerApp.comments : undefined,
      creditScore: dealerApp.creditScore ? dealerApp.creditScore : undefined,
    };
    props.accept && props.accept(massagedDealerApp);
  };

  return (
    <div className={styles.dealerAppFormPageContainer} id='dealer-app-form-container'>
      <DealerApplicationForm
        dealerApplication={dealerApp}
        errors={formErrors}
        isLocationDropdownDisabled={hasValue(props.defaultLocationId)}
        customerTitles={mockCustomerTitles}
        states={states}
        applicantAddressStateIdsToFilterBy={applicantAddressStateIdsToFilterBy}
        employerAddressStateIdsToFilterBy={employerAddressStateIdsToFilterBy}
        employmentTypes={jobCodes.map(j => { return { id: j.id, name: j.name } })}
        employmentStatuses={incomeTypes.map(j => { return { id: j.id, name: j.name } })}
        locations={allOrganizations.filter(o => o.typeId === Constants.organizationTypes.storeId).map(o => { return { id: o.id, name: o.name || '' } })}
        housingStatuses={housingStatuses.map(h => { return { id: h.id, name: splitCamelCaseWord(h.name), sortOrder: h.sortOrder, description: h.description } })}
        incomePeriods={incomePeriods.map(i => { return { id: i.id, name: i.name, sortOrder: i.sortOrder, description: i.description } })}
        incomeSources={incomeTypes.map(i => { return { id: i.id, name: i.name, sortOrder: i.sortOrder, description: i.description } })}
        bookoutSources={bookoutSources}
        minAddressMonths={minMonthsAddress}
        minEmploymentMonths={minMonthsEmployment}
        usePaymentCallTransactionType={usePaymentCallTransactionType}
        isDecodingSaleVin={isDecodingVin}
        relationshipTypes={relationshipToApplicantTypes}
        hasAcceptedTerms={termsAccepted}
        suffixes={suffixes}
        disclosure={disclosureText}
        vehicleIntendedUses={mockVehicleIntendedUses}
        netIncomeLabel={configuration?.featureFlags?.[FeatureFlagIds.NetIncomeLabelNameCreditApp] || false}
        onChange={(dealerApplication: DealerApplication) => {
          // set the dealer application state
          setDealerApp(dealerApplication);
        }}
        onDecodeButtonClick={() => handleDecodeVinClick()}
        onErrorsUpdate={(errors: ApplicationFormError[]) => setFormErrors(errors)}
        onTermsAccepted={(accepted: boolean) => setTermsAccepted(accepted)}
      />
      {formErrors.length > 0 && (
        <div className={styles.formErrorRow}>
          Please complete these missing fields before submitting your application:
          <Tooltip title={<DealerFormErrorList errors={formErrors} />} placement="top-end" >
            <Button color='error' style={{ textTransform: 'none' }} variant='outlined'>
              <ErrorOutlineOutlinedIcon color='error' style={{ marginRight: '5px' }} />
              Missing Fields
            </Button>
          </Tooltip>
        </div>
      )}
      <div className={styles.formActions}>
        <Button autoFocus onClick={props.closeDialog} color='primary' variant='outlined'>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          disabled={!canSubmitForm() || disableSave}
          onClick={() => {
            setdisabledSave(true)
            handleSubmitClick();
          }}
        >
          <CheckCircleOutlineOutlinedIcon style={{ marginRight: '5px' }} />
          Submit Application
        </Button>
      </div>
      <Snackbar
        open={isDecodeSnackbarOpen}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setIsDecodeSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setIsDecodeSnackbarOpen(false)}
          severity="info"
          sx={{ width: '100%' }}
        >
          {decodeSnackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}